import type { JobMarketDto } from 'src/contracts/job-market';

import { useState, useEffect, useCallback } from 'react';

import { extensionService } from 'src/lib/extension';

export type ScrapingStatus = 'idle' | 'scraping' | 'processing' | 'complete' | 'error';

interface ScrapingStatusInfo {
  status: ScrapingStatus;
  message: string;
  detail?: string;
  progress?: number;
}

interface UseExtensionCommunicationReturn {
  scrapingStatus: ScrapingStatusInfo;
  jobs: JobMarketDto[];
  setJobs: (jobs: JobMarketDto[]) => void;
}

/**
 * Hook for communicating with the JobJourney Chrome extension
 */
export function useExtensionCommunication(): UseExtensionCommunicationReturn {
  const [jobs, setJobsState] = useState<JobMarketDto[]>([]);
  const [scrapingStatus] = useState<ScrapingStatusInfo>({
    status: 'idle',
    message: 'Ready to start scraping',
  });

  /**
   * Set jobs with session storage sync
   */
  const setJobs = useCallback((newJobs: JobMarketDto[]) => {
    setJobsState(newJobs);
  }, []);

  /**
   * Initialize the communication service
   */
  useEffect(() => {
    // Initialize with debug mode only in development
    const initService = async () => {
      try {
        console.log('🚀 Initializing extension service...');
        await extensionService.initialize({ debug: true });
        console.log('✅ Extension service initialized');
      } catch (error) {
        console.error('❌ Error initializing extension service:', error);
      }
    };

    initService();
  }, []);

  /**
   * Listen for extension jobs processed events
   * Note: This is now handled by the extension service in JobMarketPage
   * Keeping this disabled to prevent duplicate processing
   */
  // useEffect(() => {
  //   const handleExtensionJobsProcessed = (event: CustomEvent) => {
  //     try {
  //       const { jobs: processedJobs } = event.detail;

  //       if (Array.isArray(processedJobs) && processedJobs.length > 0) {
  //         console.log(`Hook: Received ${processedJobs.length} processed jobs from extension`);

  //         // Clear session storage first to ensure we don't mix old and new jobs
  //         clearSessionJobs();

  //         // Set the new jobs
  //         setJobsState(processedJobs);

  //         setScrapingStatus({
  //           status: 'complete',
  //           message: `Received ${processedJobs.length} jobs from scraping`,
  //         });
  //       }
  //     } catch (error) {
  //       console.error('Hook: Error handling extension jobs:', error);
  //     }
  //   };

  //   // Register listeners
  //   window.addEventListener(
  //     'extension-jobs-processed',
  //     handleExtensionJobsProcessed as EventListener
  //   );

  //   // Cleanup
  //   return () => {
  //     window.removeEventListener(
  //       'extension-jobs-processed',
  //       handleExtensionJobsProcessed as EventListener
  //     );
  //   };
  // }, []);

  return {
    scrapingStatus,
    jobs,
    setJobs,
  };
}
